import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './Sidebar.module.css';

interface SidebarProps {
  lang: 'en' | 'it';
  setLang: React.Dispatch<React.SetStateAction<'en' | 'it'>>;
}

/**
 * Sidebar Component
 * 
 * Displays the main navigation sidebar on desktop and transforms into a top bar on mobile.
 * Contains the logo, navigation links, and the language toggle button.
 */
const Sidebar: React.FC<SidebarProps> = ({ lang, setLang }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isCaseStudy = location.pathname.startsWith('/project/');

  // Toggle between English and Italian
  const handleLangToggle = () => {
    setLang((prev) => (prev === 'en' ? 'it' : 'en'));
  };

  // Handle section navigation (supports smooth scroll on homepage and route change from subpages)
  const handleSectionClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  return (
    <aside className={styles.sidebar}>
      {/* Top Section: Logo */}
      <div className={styles.topSection}>
        <Link to="/" className={styles.logo} aria-label="Lorenzo Quadrelli Home">
          LQ
        </Link>
      </div>

      {/* Center Section: Navigation Links (Hidden on Mobile) */}
      <div className={styles.centerSection}>
        <div className={styles.contentsList}>
          <div className={styles.contentsLabel}>
            {isCaseStudy ? (lang === 'en' ? 'Case Study' : 'Progetto') : (lang === 'en' ? 'Contents' : 'Indice')}
          </div>

          {isCaseStudy && (
            <a
              href="/#projects"
              onClick={(e) => handleSectionClick(e, 'projects')}
              className={styles.contentItem}
              style={{ color: 'var(--color-accent-primary)' }}
            >
              <span className={styles.contentNumber}>&larr;</span>
              <span className={styles.contentText}>
                {lang === 'en' ? 'All Projects' : 'Tutti i Progetti'}
              </span>
            </a>
          )}

          <a
            href="/#projects"
            onClick={(e) => handleSectionClick(e, 'projects')}
            className={styles.contentItem}
          >
            <span className={styles.contentNumber}>01</span>
            <span className={styles.contentText}>{lang === 'en' ? 'Projects' : 'Progetti'}</span>
          </a>

          <a
            href="/#curriculum"
            onClick={(e) => handleSectionClick(e, 'curriculum')}
            className={styles.contentItem}
          >
            <span className={styles.contentNumber}>02</span>
            <span className={styles.contentText}>{lang === 'en' ? 'Curriculum' : 'Curriculum'}</span>
          </a>

          <a
            href="/#contact"
            onClick={(e) => handleSectionClick(e, 'contact')}
            className={styles.contentItem}
          >
            <span className={styles.contentNumber}>03</span>
            <span className={styles.contentText}>{lang === 'en' ? 'Contact' : 'Contatti'}</span>
          </a>
        </div>
      </div>

      {/* Bottom Section: Language Toggle */}
      <div className={styles.bottomSection}>
        <button onClick={handleLangToggle} className={styles.langButton} aria-label="Toggle language">
          {lang === 'en' ? 'English' : 'Italiano'}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
